import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class StartingNumber extends BaseObj {
    public ID: number;
    public __id: number;

    public Bay: number | null;
    public __bay: number | null;

    public Bib: number;
    public __bib: number;

    public Chip1: string | null;
    public __chip1: string | null;

    public Chip2: string | null;
    public __chip2: string | null;

    public ContestantID: number | null;
    public __contestantid: number | null;

    public DeviceImei: string | null;
    public __deviceimei: string | null;

    public DeviceText: string | null;
    public __devicetext: string | null;

    public EventID: number;
    public __eventid: number;

    public IsBlocked: boolean;
    public __isblocked: boolean;

    public StartTime: Date | null;
    public __starttime: Date | null;

    public Target: number | null;
    public __target: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Bay?: number | null;
        Bib?: number;
        Chip1?: string | null;
        Chip2?: string | null;
        ContestantID?: number | null;
        DeviceImei?: string | null;
        DeviceText?: string | null;
        EntityState?: EntityState;
        EventID?: number;
        IsBlocked?: boolean;
        StartTime?: Date | null;
        Target?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<StartingNumber>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('StartingNumber tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Bay = values.Bay ?? null;
        this.__bay = values.Bay ?? null;
        Object.defineProperty(this, 'Bay', {
            get(): number | null {
                return this.__bay;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Bay', this.__bay, value);
                this.__bay = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Bib = values.Bib ?? 0;
        this.__bib = values.Bib ?? 0;
        Object.defineProperty(this, 'Bib', {
            get(): number {
                return this.__bib;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Bib', this.__bib, value);
                this.__bib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Chip1 = values.Chip1 ?? null;
        this.__chip1 = values.Chip1 ?? null;
        Object.defineProperty(this, 'Chip1', {
            get(): string | null {
                return this.__chip1;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Chip1', this.__chip1, value);
                this.__chip1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Chip2 = values.Chip2 ?? null;
        this.__chip2 = values.Chip2 ?? null;
        Object.defineProperty(this, 'Chip2', {
            get(): string | null {
                return this.__chip2;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Chip2', this.__chip2, value);
                this.__chip2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantID = values.ContestantID ?? null;
        this.__contestantid = values.ContestantID ?? null;
        Object.defineProperty(this, 'ContestantID', {
            get(): number | null {
                return this.__contestantid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ContestantID', this.__contestantid, value);
                this.__contestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DeviceImei = values.DeviceImei ?? null;
        this.__deviceimei = values.DeviceImei ?? null;
        Object.defineProperty(this, 'DeviceImei', {
            get(): string | null {
                return this.__deviceimei;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('DeviceImei', this.__deviceimei, value);
                this.__deviceimei = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.DeviceText = values.DeviceText ?? null;
        this.__devicetext = values.DeviceText ?? null;
        Object.defineProperty(this, 'DeviceText', {
            get(): string | null {
                return this.__devicetext;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('DeviceText', this.__devicetext, value);
                this.__devicetext = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IsBlocked = values.IsBlocked ?? false;
        this.__isblocked = values.IsBlocked ?? false;
        Object.defineProperty(this, 'IsBlocked', {
            get(): boolean {
                return this.__isblocked;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsBlocked', this.__isblocked, value);
                this.__isblocked = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartTime = values.StartTime ?? null;
        this.__starttime = values.StartTime ?? null;
        Object.defineProperty(this, 'StartTime', {
            get(): Date | null {
                return this.__starttime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('StartTime', this.__starttime, value);
                this.__starttime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Target = values.Target ?? null;
        this.__target = values.Target ?? null;
        Object.defineProperty(this, 'Target', {
            get(): number | null {
                return this.__target;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Target', this.__target, value);
                this.__target = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Bay": ${JSON.stringify(this.Bay)}` +
            ',' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"Chip1": ${JSON.stringify(this.Chip1)}` +
            ',' +
            `"Chip2": ${JSON.stringify(this.Chip2)}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"DeviceImei": ${JSON.stringify(this.DeviceImei)}` +
            ',' +
            `"DeviceText": ${JSON.stringify(this.DeviceText)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"IsBlocked": ${JSON.stringify(this.IsBlocked)}` +
            ',' +
            `"StartTime": ${this.StartTime ? '"' + formatDateIso(this.StartTime) + '"' : null}` +
            ',' +
            `"Target": ${JSON.stringify(this.Target)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of StartingNumber from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Bay?: number | null;
        Bib?: number;
        Chip1?: string | null;
        Chip2?: string | null;
        ContestantID?: number | null;
        DeviceImei?: string | null;
        DeviceText?: string | null;
        EventID?: number;
        IsBlocked?: boolean;
        StartTime?: Date | null;
        Target?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): StartingNumber {
        return new StartingNumber(values);
    }

    public static _FromServerJson(json: any): StartingNumber {
        return new StartingNumber({
            ID: json.ID,
            Bay: json.Bay ?? null,
            Bib: json.Bib,
            Chip1: json.Chip1 ?? '',
            Chip2: json.Chip2 ?? '',
            ContestantID: json.ContestantID ?? null,
            DeviceImei: json.DeviceImei ?? '',
            DeviceText: json.DeviceText ?? '',
            EventID: json.EventID,
            IsBlocked: json.IsBlocked,
            StartTime: json.StartTime ? (json.StartTime instanceof Date ? json.StartTime : new Date(json.StartTime)) : null,
            Target: json.Target ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of StartingNumber from another instance of StartingNumber.
     * @param other The StartingNumber you want to clone
     * @returns A cloned StartingNumber
     */
    public static FromOther(other: StartingNumber): StartingNumber {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return StartingNumber._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return StartingNumber.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Bay'
            | 'Bib'
            | 'Chip1'
            | 'Chip2'
            | 'ContestantID'
            | 'DeviceImei'
            | 'DeviceText'
            | 'EventID'
            | 'IsBlocked'
            | 'StartTime'
            | 'Target'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'StartingNumber';
}
